import { useCallback, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";

// ── Storage keys ─────────────────────────────────────────────────
const UTM_STORAGE_KEY = "ap_utms";
const SESSION_KEY = "ap_session_id";
const SESSION_TS_KEY = "ap_session_ts";
const VISITOR_KEY = "ap_visitor_id";
const LANDING_PAGE_KEY = "ap_landing_page";
const INTERNAL_FLAG_KEY = "ap_is_internal";
const SESSION_TIMEOUT_MS = 30 * 60 * 1000; // 30-min inactivity

interface StoredUtms {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
  gclid?: string;
  fbclid?: string;
  li_fat_id?: string;
  referrer?: string;
}

// ── ID helpers ───────────────────────────────────────────────────
function generateId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

function getVisitorId(): string {
  if (typeof window === "undefined") return "";
  try {
    let id = localStorage.getItem(VISITOR_KEY);
    if (!id) {
      id = generateId();
      localStorage.setItem(VISITOR_KEY, id);
    }
    return id;
  } catch {
    return generateId();
  }
}

function getSessionId(): string {
  if (typeof window === "undefined") return "";
  try {
    const now = Date.now();
    const lastTsRaw = sessionStorage.getItem(SESSION_TS_KEY);
    const lastTs = lastTsRaw ? Number(lastTsRaw) : 0;
    let id = sessionStorage.getItem(SESSION_KEY);
    // Roll session if expired or missing
    if (!id || (lastTs && now - lastTs > SESSION_TIMEOUT_MS)) {
      id = generateId();
      sessionStorage.setItem(SESSION_KEY, id);
    }
    sessionStorage.setItem(SESSION_TS_KEY, String(now));
    return id;
  } catch {
    return generateId();
  }
}

function getLandingPage(): string | null {
  if (typeof window === "undefined") return null;
  try {
    let lp = sessionStorage.getItem(LANDING_PAGE_KEY);
    if (!lp) {
      lp = window.location.pathname;
      sessionStorage.setItem(LANDING_PAGE_KEY, lp);
    }
    return lp;
  } catch {
    return window.location.pathname;
  }
}

function extractReferrerDomain(ref: string | undefined): string | null {
  if (!ref) return null;
  try {
    const u = new URL(ref);
    if (u.hostname && !u.hostname.includes(window.location.hostname)) {
      return u.hostname.replace(/^www\./, "");
    }
    return null;
  } catch {
    return null;
  }
}

// ── Bot / internal detection ─────────────────────────────────────
const BOT_RE =
  /bot|crawl|spider|slurp|preview|lighthouse|headless|chrome-lighthouse|google-inspection|facebookexternalhit|whatsapp|linkedinbot|twitterbot|slackbot|discordbot|telegrambot|monitor|pingdom|uptime/i;

function isBot(): boolean {
  if (typeof navigator === "undefined") return false;
  if ((navigator as any).webdriver === true) return true;
  return BOT_RE.test(navigator.userAgent || "");
}

function isInternal(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return localStorage.getItem(INTERNAL_FLAG_KEY) === "1";
  } catch {
    return false;
  }
}

/** Mark this browser as internal traffic (called once for admins). */
export function markInternalTraffic(value: boolean) {
  try {
    if (value) localStorage.setItem(INTERNAL_FLAG_KEY, "1");
    else localStorage.removeItem(INTERNAL_FLAG_KEY);
  } catch {
    // ignore
  }
}

// ── UTM persistence ──────────────────────────────────────────────
function captureAndPersistUtms(): StoredUtms {
  if (typeof window === "undefined") return {};

  const params = new URLSearchParams(window.location.search);
  const currentUtms: StoredUtms = {
    utm_source: params.get("utm_source") || undefined,
    utm_medium: params.get("utm_medium") || undefined,
    utm_campaign: params.get("utm_campaign") || undefined,
    utm_content: params.get("utm_content") || undefined,
    utm_term: params.get("utm_term") || undefined,
    gclid: params.get("gclid") || undefined,
    fbclid: params.get("fbclid") || undefined,
    li_fat_id: params.get("li_fat_id") || undefined,
    referrer: document.referrer || undefined,
  };

  const hasMarketingParams =
    currentUtms.utm_source ||
    currentUtms.utm_medium ||
    currentUtms.utm_campaign ||
    currentUtms.gclid ||
    currentUtms.fbclid ||
    currentUtms.li_fat_id;

  if (hasMarketingParams) {
    try {
      localStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(currentUtms));
    } catch {
      // ignore
    }
    return currentUtms;
  }

  try {
    const stored = localStorage.getItem(UTM_STORAGE_KEY);
    if (stored) return JSON.parse(stored);
  } catch {
    // ignore
  }

  if (currentUtms.referrer) {
    try {
      localStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(currentUtms));
    } catch {
      // ignore
    }
  }

  return currentUtms;
}

export function getPersistedUtms(): StoredUtms {
  try {
    const stored = localStorage.getItem(UTM_STORAGE_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
}

// ── Core tracking function ───────────────────────────────────────
function buildBeaconUrl(): string {
  const projectId = (import.meta as any).env?.VITE_SUPABASE_PROJECT_ID;
  const base = projectId
    ? `https://${projectId}.supabase.co`
    : (import.meta as any).env?.VITE_SUPABASE_URL || "";
  return `${base}/functions/v1/track-pageview`;
}

function sendBeacon(payload: Record<string, unknown>): boolean {
  try {
    if (typeof navigator === "undefined" || !navigator.sendBeacon) return false;
    const url = buildBeaconUrl();
    if (!url) return false;
    const blob = new Blob([JSON.stringify(payload)], { type: "application/json" });
    return navigator.sendBeacon(url, blob);
  } catch {
    return false;
  }
}

export async function trackEvent(
  eventName: string,
  properties?: Record<string, unknown>,
) {
  try {
    if (typeof window === "undefined") return;

    const utms = captureAndPersistUtms();
    const sessionId = getSessionId();
    const visitorId = getVisitorId();
    const landingPage = getLandingPage();
    const bot = isBot();
    const internal = isInternal();
    const userAgent = navigator.userAgent || null;
    const referrerDomain = extractReferrerDomain(utms.referrer);
    const { data: { user } } = await supabase.auth.getUser();

    const row = {
      event_name: eventName,
      user_id: user?.id || null,
      event_properties: properties || {},
      utm_source: utms.utm_source || null,
      utm_medium: utms.utm_medium || null,
      utm_campaign: utms.utm_campaign || null,
      utm_content: utms.utm_content || null,
      utm_term: utms.utm_term || null,
      gclid: utms.gclid || null,
      fbclid: utms.fbclid || null,
      li_fat_id: utms.li_fat_id || null,
      referrer: utms.referrer || null,
      referrer_domain: referrerDomain,
      session_id: sessionId,
      visitor_id: visitorId,
      landing_page: landingPage,
      page_url: window.location.pathname,
      user_agent: userAgent,
      is_bot: bot,
      is_internal: internal,
    };

    const { error } = await supabase.from("analytics_events" as any).insert(row);
    if (error) {
      // Direct insert blocked (ad-blocker, RLS, network) — fall back to public beacon
      sendBeacon(row);
    }
  } catch (err) {
    console.warn("[analytics] tracking error:", err);
  }
}

/** Fire on page-unload — survives navigation away. */
export function trackEventBeacon(
  eventName: string,
  properties?: Record<string, unknown>,
) {
  if (typeof window === "undefined") return;
  try {
    const utms = getPersistedUtms();
    sendBeacon({
      event_name: eventName,
      event_properties: properties || {},
      session_id: getSessionId(),
      visitor_id: getVisitorId(),
      landing_page: getLandingPage(),
      page_url: window.location.pathname,
      referrer: utms.referrer || null,
      utm_source: utms.utm_source || null,
      utm_medium: utms.utm_medium || null,
      utm_campaign: utms.utm_campaign || null,
      gclid: utms.gclid || null,
      fbclid: utms.fbclid || null,
      li_fat_id: utms.li_fat_id || null,
      is_internal: isInternal(),
    });
  } catch {
    // ignore
  }
}

// ── React hooks ──────────────────────────────────────────────────
export function useAnalyticsTracking() {
  const track = useCallback(
    (eventName: string, properties?: Record<string, unknown>) =>
      trackEvent(eventName, properties),
    [],
  );
  return { track };
}

/**
 * @deprecated Use RouteVisitTracker (mounted once at the app root) for
 * visit_landing emission. This hook is kept as a no-op for backwards
 * compatibility — calling it does NOT fire visit_landing anymore.
 */
export function usePageVisitTracking(_pageName: string) {
  // intentionally empty — RouteVisitTracker is the single source of truth
}

/** Track CTA clicks */
export function useCtaTracking() {
  return useCallback(
    (ctaName: string, extra?: Record<string, unknown>) =>
      trackEvent("cta_clicked", { cta: ctaName, ...extra }),
    [],
  );
}

// ── Onboarding step instrumentation ──────────────────────────────
export type OnboardingStepStatus = "started" | "completed" | "failed" | "abandoned";

export function trackOnboardingStep(
  step: string,
  status: OnboardingStepStatus,
  meta?: Record<string, unknown>,
) {
  if (import.meta.env.DEV) {
    console.debug(`[onboarding] ${step} → ${status}`, meta || {});
  }
  return trackEvent("onboarding_step", { step, status, ...(meta || {}) });
}

// ── Route-level visit tracker ────────────────────────────────────
// Skip app shell, admin, auth callbacks, and unsubscribe — those aren't
// "marketing visits" we want to count toward visitor totals.
const PRIVATE_PREFIXES = [
  "/admin",
  "/inbox",
  "/train",
  "/prospects",
  "/conversations",
  "/pipeline",
  "/analytics",
  "/settings",
  "/sending-health",
  "/signal-discovery-preview",
  "/meetings",
  "/approvals",
  "/super-brain",
  "/onboarding",
  "/accept-invite",
  "/auth/calendar",
  "/auth/graph",
  "/unsubscribe",
];

function isPrivateRoute(pathname: string): boolean {
  return PRIVATE_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

/**
 * Mount once near the app root. Fires `visit_landing` on every public-route
 * change (deduped by visitor+session+page_url so SPA navigation, React
 * StrictMode double-mounts, and remounts don't inflate counts). The dedup
 * key uses the exact same value inserted into analytics_events.page_url.
 */
export function RouteVisitTracker() {
  const location = useLocation();

  useEffect(() => {
    const path = location.pathname; // identical to row.page_url in trackEvent()
    if (isPrivateRoute(path)) return;

    try {
      const ssKey = `ap_visit_landing:${getVisitorId()}::${getSessionId()}::${path}`;
      if (typeof sessionStorage !== "undefined") {
        if (sessionStorage.getItem(ssKey)) return;
        sessionStorage.setItem(ssKey, "1");
      }
    } catch {
      /* ignore */
    }

    captureAndPersistUtms();
    trackEvent("visit_landing", { page: path });
  }, [location.pathname]);

  return null;
}

// ── Article read tracker ─────────────────────────────────────────
/**
 * Fires `blog_article_read` once when the user has spent ≥ minSeconds on the
 * page AND scrolled past `scrollThreshold` (0–1). Use on blog/programmatic
 * pages to measure quality engagement.
 */
export function useArticleReadTracking(
  slug: string | undefined,
  options: { minSeconds?: number; scrollThreshold?: number; extra?: Record<string, unknown> } = {},
) {
  const { minSeconds = 30, scrollThreshold = 0.5, extra } = options;
  const fired = useRef(false);
  const scrollHit = useRef(false);
  const startedAt = useRef<number>(Date.now());

  useEffect(() => {
    if (!slug) return;
    fired.current = false;
    scrollHit.current = false;
    startedAt.current = Date.now();

    const tryFire = () => {
      if (fired.current) return;
      const elapsed = (Date.now() - startedAt.current) / 1000;
      if (elapsed >= minSeconds && scrollHit.current) {
        fired.current = true;
        trackEvent("blog_article_read", { slug, seconds: Math.round(elapsed), ...(extra || {}) });
      }
    };

    const onScroll = () => {
      const doc = document.documentElement;
      const max = doc.scrollHeight - window.innerHeight;
      if (max <= 0) { scrollHit.current = true; tryFire(); return; }
      const ratio = window.scrollY / max;
      if (ratio >= scrollThreshold) { scrollHit.current = true; tryFire(); }
    };

    const interval = window.setInterval(tryFire, 5000);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();

    return () => {
      window.clearInterval(interval);
      window.removeEventListener("scroll", onScroll);
    };
  }, [slug, minSeconds, scrollThreshold]);
}
